import { useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * Lightweight SEO helper for React (no react-helmet needed).
 *
 *  Usage:
 *      <SEO
 *          title={{ en: "...", hi: "...", ar: "..." }}     // or string
 *          description={{ en: "...", hi: "...", ar: "..." }}
 *          path="/cabs"
 *          jsonLd={{...}}                                    // schema.org object or array
 *          image="https://southtrip.in/og.jpg"
 *          noindex                                           // pages transactionnelles : robots="noindex, follow"
 *      />
 *
 *  • Picks the language-specific title/description (falls back to en).
 *  • Updates <title>, <meta name="description">, OG/Twitter, canonical and hreflang.
 *  • Injects JSON-LD <script type="application/ld+json">.
 *  • Cleans up on unmount.
 */
const SITE = "https://southtrip.in";
const DEFAULT_OG_IMAGE = "https://southtrip.in/og-cover-1200x630.jpg";
const ROBOTS_INDEX = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

const setMeta = (selector, attr, value) => {
    if (typeof document === "undefined") return;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        const [, key, val] = selector.match(/\[([^=]+)="([^"]+)"\]/) || [];
        if (key && val) el.setAttribute(key, val);
        document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
};

const setLink = (rel, href, lang) => {
    if (typeof document === "undefined") return;
    const sel = lang
        ? `link[rel="${rel}"][hreflang="${lang}"]`
        : `link[rel="${rel}"]:not([hreflang])`;
    let el = document.head.querySelector(sel);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (lang) el.setAttribute("hreflang", lang);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
};

const pick = (obj, lng) => {
    if (!obj) return "";
    if (typeof obj === "string") return obj;
    return obj[lng] || obj.en || Object.values(obj)[0] || "";
};

const SEO = ({ title, description, path = "", image, jsonLd, keywords, type = "website", noindex = false }) => {
    const { i18n } = useTranslation();
    const lng = (i18n.language || "en").split("-")[0];

    useEffect(() => {
        const titleStr = pick(title, lng);
        const descStr = pick(description, lng);
        const url = `${SITE}${path}`;
        const img = image || DEFAULT_OG_IMAGE;

        setMeta('meta[name="robots"]', "content", noindex ? "noindex, follow" : ROBOTS_INDEX);
        if (titleStr) document.title = titleStr;
        if (descStr) setMeta('meta[name="description"]', "content", descStr);
        if (keywords) setMeta('meta[name="keywords"]', "content", typeof keywords === "string" ? keywords : keywords.join(", "));

        // Open Graph + Twitter
        if (titleStr) {
            setMeta('meta[property="og:title"]', "content", titleStr);
            setMeta('meta[name="twitter:title"]', "content", titleStr);
        }
        if (descStr) {
            setMeta('meta[property="og:description"]', "content", descStr);
            setMeta('meta[name="twitter:description"]', "content", descStr);
        }
        setMeta('meta[property="og:url"]', "content", url);
        setMeta('meta[property="og:type"]', "content", type);
        setMeta('meta[property="og:image"]', "content", img);
        setMeta('meta[name="twitter:image"]', "content", img);
        const localeMap = { en: "en_IN", hi: "hi_IN", ml: "ml_IN", ta: "ta_IN", ar: "ar_AE" };
        setMeta('meta[property="og:locale"]', "content", localeMap[lng] || "en_IN");

        // Canonical + hreflang
        setLink("canonical", url);
        setLink("alternate", url, "en");
        setLink("alternate", `${SITE}${path}?lng=hi`, "hi");
        setLink("alternate", `${SITE}${path}?lng=ml`, "ml");
        setLink("alternate", `${SITE}${path}?lng=ta`, "ta");
        setLink("alternate", `${SITE}${path}?lng=ar`, "ar");
        setLink("alternate", url, "x-default");

        // JSON-LD per-page
        const SCRIPT_ID = `seo-jsonld-${path || "root"}`;
        document.querySelectorAll(`script[data-seo-id="${SCRIPT_ID}"]`).forEach((n) => n.remove());
        if (jsonLd) {
            const script = document.createElement("script");
            script.type = "application/ld+json";
            script.dataset.seoId = SCRIPT_ID;
            script.textContent = JSON.stringify(jsonLd);
            document.head.appendChild(script);
        }
        return () => {
            document
                .querySelectorAll(`script[data-seo-id="${SCRIPT_ID}"]`)
                .forEach((n) => n.remove());
        };
    }, [title, description, path, image, jsonLd, keywords, type, noindex, lng]);

    return null;
};

export default SEO;
